import { Bound } from '@blocksuite/global/gfx';
import type {
  GfxCompatibleProps,
  GfxElementGeometry,
  GfxGroupCompatibleInterface,
  GfxModel,
  PointTestOptions,
} from '@blocksuite/std/gfx';
import {
  canSafeAddToContainer,
  descendantElementsImpl,
  GfxCompatible,
  gfxGroupCompatibleSymbol,
  groupMembers,
  hasDescendantElementImpl,
  loadedDescendantElementsImpl,
  loadedGroupMembers,
} from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
  type Text,
} from '@blocksuite/store';
import { generateFractionalIndexingKeyBetween } from '@streamlain/database-identity';
import { z } from 'zod';

import { type Color, ColorSchema, DefaultTheme } from '../../themes/index.js';

/**
 * Fields of a frame. Its members and its presentation index live in the
 * page structure: the members' g and the frame's pi. Snapshots carry them
 * as `childElementIds` and `presentationIndex`.
 */
export type FrameBlockProps = {
  title: Text;
  background: Color;
  comments?: Record<string, boolean>;
} & GfxCompatibleProps;

/** Fields of a frame in a snapshot, with its members and presentation index. */
export type FrameSnapshotProps = FrameBlockProps & {
  childElementIds: Record<string, boolean>;
  presentationIndex: string;
};

/** Presentation index of a frame created without one. */
export const DEFAULT_FRAME_PRESENTATION_INDEX =
  generateFractionalIndexingKeyBetween(null, null);

export const FrameZodSchema = z
  .object({
    background: ColorSchema,
  })
  .prefault({
    background: DefaultTheme.transparent,
  });

export const FrameBlockSchema = defineBlockSchema({
  flavour: 'affine:frame',
  props: (internal): FrameBlockProps => ({
    title: internal.Text(),
    background: 'transparent',
    xywh: `[0,0,100,100]`,
    index: 'a0',
    lockedBySelf: false,
    comments: undefined,
  }),
  metadata: {
    version: 1,
    role: 'content',
    parent: ['affine:surface'],
    children: [],
    structure: {
      members: 'childElementIds',
      presentationIndex: 'presentationIndex',
    },
  },
  toModel: () => {
    return new FrameBlockModel();
  },
});

export const FrameBlockSchemaExtension = BlockSchemaExtension(FrameBlockSchema);

export class FrameBlockModel
  extends GfxCompatible<FrameBlockProps>(BlockModel)
  implements GfxElementGeometry, GfxGroupCompatibleInterface
{
  [gfxGroupCompatibleSymbol] = true as const;

  readonly stacksChildren = false;

  get childElements(): GfxModel[] {
    if (!this.surface) return [];
    return groupMembers(this.id, this.surface, this.childIds);
  }

  get loadedChildElements(): GfxModel[] {
    if (!this.surface) return [];
    return loadedGroupMembers(this.surface, this.childIds);
  }

  get childIds(): string[] {
    return [...this.store.pageStructure.members(this.id)];
  }

  /** Place of the frame in the presentation order. */
  get presentationIndex(): string {
    return (
      this.store.pageStructure.entry(this.id)?.pi ??
      DEFAULT_FRAME_PRESENTATION_INDEX
    );
  }

  set presentationIndex(pi: string) {
    this.store.transact(() =>
      this.store.pageStructure.writer.setPresentationIndex(this.id, pi)
    );
  }

  get descendantElements(): GfxModel[] {
    return descendantElementsImpl(this);
  }

  get loadedDescendantElements(): GfxModel[] {
    return loadedDescendantElementsImpl(this);
  }

  addChild(element: GfxModel) {
    if (!canSafeAddToContainer(this, element)) return;

    this.store.transact(() => {
      this.store.pageStructure.join(element.id, this.id);
    });

    // Set parent reference for O(1) lookup
    if ('_parentGroupId' in element) {
      element._parentGroupId = this.id;
    }
  }

  addChildren(elements: GfxModel[]): void {
    elements = [...new Set(elements)].filter(element =>
      canSafeAddToContainer(this, element)
    );

    this.store.transact(() => {
      for (const element of elements) {
        this.store.pageStructure.join(element.id, this.id);
      }
    });

    // Set parent reference for O(1) lookup
    for (const element of elements) {
      if ('_parentGroupId' in element) {
        element._parentGroupId = this.id;
      }
    }
  }

  override containsBound(bound: Bound): boolean {
    return this.elementBound.contains(bound);
  }

  hasChild(element: GfxModel): boolean {
    return this.store.pageStructure.groupOf(element.id) === this.id;
  }

  hasDescendant(element: GfxModel): boolean {
    return hasDescendantElementImpl(this, element);
  }

  override includesPoint(x: number, y: number, _: PointTestOptions): boolean {
    const bound = Bound.deserialize(this.transientXYWH ?? this.xywh);
    return bound.isPointInBound([x, y]);
  }

  override intersectsBound(selectedBound: Bound): boolean {
    const bound = Bound.deserialize(this.transientXYWH ?? this.xywh);
    return (
      bound.isIntersectWithBound(selectedBound) || selectedBound.contains(bound)
    );
  }

  removeChild(element: GfxModel): void {
    if (!this.hasChild(element)) return;
    this.store.transact(() => {
      this.store.pageStructure.leave(element.id);
    });

    // Clear parent reference
    if ('_parentGroupId' in element) {
      element._parentGroupId = null;
    }
  }
}
